import { Component, OnInit, inject, viewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Position } from 'ngx-mapbox-gl';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import {
  CreateZoneCommand,
  CustomResultOfZoneDto,
  GeometryType,
  UpdateZoneCommand,
  ZoneDto,
  ZoneType,
} from 'src/app/core/api/models';
import { ZonesService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { ZoneTypeDisplay } from 'src/app/core/enums/display/zone-type.display';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { ConsoleService } from 'src/app/core/services/console.service';
import {
  DialogService,
  DialogStatus,
} from 'src/app/core/services/dialog.service';
import { PublicPlaceSelectSearchComponent } from 'src/app/shared/components/select-search/public-places-select-search.component';
import { SmartSendMapControlComponent } from 'src/app/shared/components/smart-send-map-control/smart-send-map-control.component';
import { BaseFormPageComponent } from '../../../shared/base/components/base-form-page.component';
import { FormPageConfig } from './../../../../core/models/form-page-config.model';
import { BaseFormModule } from 'src/app/pages/shared/base/modules/base-form.module';


@Component({
    selector: 'app-zones-form',
    templateUrl: './zones-form.component.html',
    imports: [BaseFormModule]
})
export class ZonesFormComponent
  extends BaseFormPageComponent
  implements OnInit
{
  appStateService = inject(AppStateService);
  protected currentService = inject(ZonesService);
  //#region Properties

  formPageConfig: FormPageConfig<any> = {};
  model: ZoneDto = {};
  zoneTypeForSelectSearch: ZoneType;

  get GeometryType() {
    return GeometryType;
  }

  //zones
  selectedState;

  stateId: number = 0;
  cityId: number = -1;

  publicPlaceSelect: PublicPlaceSelectSearchComponent;
  readonly Map = viewChild<SmartSendMapControlComponent>('Map');

  publicPlaceId: number | null = null;
  selectedPlacePolygon: string;

  get ZoneType() {
    return ZoneType;
  }

  public ZoneTypeDisplay: typeof ZoneTypeDisplay = ZoneTypeDisplay;

  //#endregion

  constructor(
    private zoneService: ZonesService,
    public dialogService: DialogService,
    public translate: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.changeValidationForm();
      this.changeZoneType(this.model.type);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  //#region init methods

  initForm() {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      type: new FormControl('', Validators.required),
      parentId: new FormControl(''),
    });
  }

  initModel() {
    try {
      // when form is edit mode
      if (this.formPageConfig.isEditMode) {
        this.model = Object.assign({}, this.formPageConfig.editData);
      }
      if (this.model.parentId) this.getSelectedState(this.model.parentId);
      // when form is create mode
      else this.formPageConfig.editData = {};
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  changeValidationForm() {
    try {
      this.form.get('parentId').clearValidators();

      if (this.model.type != ZoneType.Country)
        this.form.get('parentId').setValidators([Validators.required]);

      this.form.get('parentId').updateValueAndValidity();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
  }
  onMapReady() {
    this.getCoordinate();
  }

  //#endregion

  //#region Api

  //Create
  baseCreateApi(model: CreateZoneCommand) {
    this.unSubs();

    this.loading = true;

    this.currentService
      .zonesCreatePost({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //Update
  baseUpdateApi(model: UpdateZoneCommand) {
    this.unSubs();

    this.loading = true;

    this.currentService
      .zonesUpdatePut({ id: this.model.id.toString(), body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //#endregion

  onSubmit() {
    try {
      if (!this.isValidForm(this.form, this.tagName)) return;
      if (this.loading) return;

      this.Map().onSaveButtonClick(null);

      this.checkCreateOrUpdate();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  //#endregion

  getSelectedState(cityId) {
    this.unSubs();

    //get city by zoneId
    this.getZoneById(cityId)
      .pipe(
        map((city: CustomResultOfZoneDto) => {
          this.selectedState = city?.data.parentId;
          return city.data;
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe();
  }

  // get zone by id
  getZoneById(zoneId: number): Observable<CustomResultOfZoneDto> {
    return this.zoneService.zonesGetZoneById({ Id: zoneId });
  }

  onChangeZones(zoneId) {
    this.model.parentId = 0;
    this.selectedState = zoneId;
    this.model.parentId = zoneId;
  }

  //#region final methods

  //#endregion

  onCityChange($event) {
    this.cityId = $event;
    this.zoneService
      .zonesGetZoneCoordinates({ ZoneId: this.cityId })
      .subscribe((result) => {
        try {
          let center = JSON.parse(result.data) as mapboxgl.LngLatLike;
          this.Map().SetCenter(center);
        } catch (error) {
          this.loggerService.logError(
            this.tagName,
            error,
            EventTypes.infrastructure
          );
        }
      });
  }

  changeZoneType(zoneType: ZoneType) {
    try {
      switch (zoneType) {
        case ZoneType.Country:
          this.model.parentId = null;
          break;
        case ZoneType.Province:
          this.zoneTypeForSelectSearch = ZoneType.Country;
          break;
        case ZoneType.City:
          //this.model.parentId = -1;
          this.zoneTypeForSelectSearch = ZoneType.Province;
          break;
        case ZoneType.Area:
          //this.model.parentId = -1;
          this.zoneTypeForSelectSearch = ZoneType.City;
          break;

        default:
          break;
      }

      this.changeValidationForm();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  public onMapSaveButtonClicked($event) {
    try {
      this.model.coordinates = $event.coordinate;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  getCoordinate() {
    try {
      if (this.model.coordinates != null) {
        let item = this.model;
        this.selectedPlacePolygon = item.coordinates;
        if (item?.coordinates) {
          let cord = JSON.parse(item.coordinates) as Position[][];
          this.Map().setEditablePolygon(cord);
          ConsoleService.info('public place is :', item);
        }
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }
  showMapHelp(event) {
    try {
      this.dialogService.openDialog({
        title: this.translate.instant('common.help'),
        message: 'messages.mapHelp',
        status: DialogStatus.info,
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }
}
